import { useState } from "react";
import { Minus, Plus, Trash2 } from "lucide-react";
import { fmtQty, roundQty } from "@/lib/qty";

/**
 * The quantity editor behind the tappable [qty] control on a catalogue card.
 *
 * Replaces the permanent ½ button: half plates are real here (quantities step
 * by 0.5, migration 047 allows two decimals), but a dedicated half-add button
 * bought one niche action a whole column of every card. ADD is always +1; this
 * dialog is where any other quantity — 0.5, 1.5, 2.5 — gets set directly.
 *
 * `allowHalf` mirrors the admin toggle captain_half_quantity_enabled: off, the
 * dialog steps whole plates and refuses fractions (the server refuses them too
 * — this is UX, the gate is order_service's).
 */
export function QtyDialog({ itemName, initial, onDone, onClose, allowHalf = true }) {
  const [value, setValue] = useState(String(initial ?? 1));

  const step = allowHalf ? 0.5 : 1;
  const parsed = roundQty(parseFloat(value));
  // Zero is a valid answer: it means "take the item off entirely" — same as
  // the bin button. Anything between 0 and one step is not.
  const valid =
    Number.isFinite(parsed) &&
    (parsed === 0 || parsed >= step) &&
    (allowHalf || parsed % 1 === 0);

  const bump = delta => {
    const next = roundQty((Number.isFinite(parsed) ? parsed : 0) + delta);
    if (next >= 0) setValue(fmtQty(next));
  };

  const done = () => {
    if (!valid) return;
    onDone(parsed);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={`Quantity for ${itemName}`}
      data-testid="qty-dialog"
    >
      <div className="bg-card w-full max-w-xs rounded-2xl shadow-xl border overflow-hidden">
        <div className="px-4 py-3 border-b">
          <strong className="text-body font-bold text-foreground block truncate">{itemName}</strong>
          <small className="text-micro font-bold uppercase text-muted-foreground">
            {allowHalf ? "Quantity · steps of 0.5" : "Quantity · whole plates (half plates are admin-only)"}
          </small>
        </div>

        {/* No autoFocus on the input: it summoned the keyboard the moment the
            dialog opened on a phone, and dismissing the keyboard read as
            dismissing the dialog. The steppers are the primary control; the
            keyboard appears only if the number itself is tapped. */}
        <div className="p-4 flex items-center justify-center gap-2">
          <button
            className="w-12 h-12 rounded-xl bg-destructive/10 text-destructive flex items-center justify-center active:scale-90 transition-transform"
            onClick={() => onDone(0)}
            aria-label={`Remove ${itemName} entirely`}
            data-testid="qty-remove"
          >
            <Trash2 className="w-5 h-5" />
          </button>
          <button
            className="w-12 h-12 rounded-xl bg-muted text-foreground flex items-center justify-center active:scale-90 transition-transform"
            onClick={() => bump(-step)}
            aria-label="Decrease quantity"
            data-testid="qty-minus"
          >
            <Minus className="w-5 h-5" />
          </button>
          <input
            type="number"
            inputMode="decimal"
            step={step}
            min={0}
            className="w-20 min-w-0 h-12 text-center text-title font-bold rounded-xl border bg-background tabular-nums focus:outline-none focus:ring-2 focus:ring-primary"
            value={value}
            onChange={e => setValue(e.target.value)}
            data-testid="qty-input"
          />
          <button
            className="w-12 h-12 rounded-xl bg-muted text-foreground flex items-center justify-center active:scale-90 transition-transform"
            onClick={() => bump(step)}
            aria-label="Increase quantity"
            data-testid="qty-plus"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 border-t divide-x">
          <button className="h-12 text-body font-bold text-muted-foreground hover:bg-muted/40" onClick={onClose}>
            Cancel
          </button>
          <button
            className="h-12 text-body font-bold text-primary hover:bg-primary/10 disabled:opacity-40"
            onClick={done}
            disabled={!valid}
            data-testid="qty-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
